import { Auth } from "@aws-amplify/auth";
import AuthenticationError from "../../common/errors/AuthenticationError";
import PresentableError from "../../common/errors/PresentableError";

import _ from "lodash";

export default function AuthenticationProvider() {
    "ngInject";

    const provider = this;

    provider.init = function (cognitoCredentials) {
        // https://docs.aws.amazon.com/cognito/latest/developerguide/cognito-user-pools-import-using-lambda.html
        Auth.configure({
            authenticationFlowType: "USER_PASSWORD_AUTH",
            mandatorySignIn: true,
            region: cognitoCredentials.REGION,
            userPoolId: cognitoCredentials.USER_POOL_ID,
            userPoolWebClientId: cognitoCredentials.APP_CLIENT_ID,
        });
    };

    provider.$get = function (
        $log,
        $q,
        $rootScope,
        $state,
        AuthError,
        PasswordResetRequiredError,
        NewPasswordRequiredError,
        toJson,
        User
    ) {
        "ngInject";

        var user;

        var ERROR_MESSAGES = {
            NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED",
        };

        var service = {
            signIn: signIn,
            confirmOneTimePasswordSignIn: confirmOneTimePasswordSignIn,
            currentSession: currentSession,
            getCurrentUser: getCurrentUser,
            signOut: signOut,
            completeNewPassword: completeNewPassword,
            requestResetPassword: requestResetPassword,
            resetPassword: resetPassword,
            signUp: signUp,
            confirmSignUp: confirmSignUp,
            goToLoginState: goToLoginState,
            currentAuthenticatedUser: currentAuthenticatedUser,
            updateUserAttributes: updateUserAttributes,
            changePassword: changePassword,
            resendVerificationEmail: resendVerificationEmail,
        };

        return service;

        function signIn(username, password) {
            const signInPromise = Auth.signIn(username, password).then(
                function (user) {
                    if (user.challengeName === "SOFTWARE_TOKEN_MFA") {
                        return $q.reject({
                            code: "MFA_REQUIRED",
                            user,
                        });
                    }

                    return Auth.currentUserInfo().then(function (userInfo) {
                        user.attributes = _.get(userInfo, "attributes");
                        return user;
                    });
                }
            );

            return _finalizeAuth(signInPromise);
        }

        function confirmOneTimePasswordSignIn(user, code) {
            const confirmPromise = Auth.confirmSignIn(
                user,
                code,
                user.challengeName
            ).then(function (confirmedUser) {
                return Auth.currentUserInfo().then(function (userInfo) {
                    confirmedUser.attributes = _.get(userInfo, "attributes");
                    return confirmedUser;
                });
            });

            return _finalizeAuth(confirmPromise);
        }

        function _finalizeAuth(authPromise) {
            return $q(function (resolve, reject) {
                authPromise
                    .then(_setUser)
                    .then(_checkIfNewPasswordRequired)
                    .then(resolve)
                    .catch(function (error) {
                        if (error.code === "MFA_REQUIRED") {
                            return reject(error);
                        }

                        if (error.code === "PasswordResetRequiredException") {
                            return reject(
                                new PasswordResetRequiredError(error.message)
                            );
                        }
                        return reject(new AuthError(error.message));
                    });
            });
        }

        function _setUser(_user) {
            user = new User(_user);
            return user;
        }

        function _checkIfNewPasswordRequired(user) {
            if (
                user._amplify.challengeName ===
                ERROR_MESSAGES.NEW_PASSWORD_REQUIRED
            ) {
                $state.go("account.verify");
                throw new NewPasswordRequiredError();
            } else {
                return user;
            }
        }

        /**
         * Get the current CognitoUserSession
         *  having accessToken, idToken and refreshToken.
         *  This method will automatically refresh the accessToken and idToken if tokens are expired and a valid
         *  refreshToken presented. So you can use this method to refresh the session if needed.
         *
         * @return {Promise<CognitoUserSession>}
         */
        function currentSession() {
            return $q(function (resolve, reject) {
                return Auth.currentSession().then(resolve, (err) => {
                    const authError = new AuthenticationError(
                        "(Legacy) Failed to get current session."
                    );
                    authError.cause = err;
                    reject(authError);
                });
            });
        }

        function getCurrentUser() {
            if (user) {
                return $q.resolve(user);
            }
            return $q
                .all([this.currentSession(), this.currentAuthenticatedUser()])
                .then(([session, user]) =>
                    _setUser(Object.assign(session, user))
                )
                .catch((error) => {
                    const authError = new AuthenticationError(
                        "(Legacy) Could not get currently logged in user or session."
                    );
                    authError.cause = error;
                    return $q.reject(authError);
                });
        }

        /**
         * Sign out the current user and optionally add redirect information for to login page
         * which allows to redirect back to the previous state after login.
         *
         * @param loginRedirect {Object} - optional object with state name and params to redirect to after login
         * @param loginRedirect.state {string} - state name to redirect after logout - default is login
         * @param loginRedirect.name {string} - state name to redirect to after login
         * @param loginRedirect.params {Object} - state params to redirect to after login
         *
         * @returns {Promise<boolean>}
         */
        function signOut(loginRedirect = {}) {
            return Auth.signOut()
                .then(() => {
                    user = null;
                    $log.debug(
                        "AuthenticationProvider::signOut - cleared memory cached user"
                    );
                })
                .then(function () {
                    $log.info(
                        "AuthenticationProvider::signOut - user signed out"
                    );
                    // introduce a logout event to let child states listen on logout and trigger needed actions.
                    $rootScope.$broadcast("sb.global.logout");
                })
                .then(() => {
                    if (loginRedirect && loginRedirect.state) {
                        goToState(loginRedirect.state);
                    } else {
                        goToLoginState(
                            loginRedirect?.name,
                            loginRedirect?.params
                        );
                    }
                });
        }

        function completeNewPassword(newPassword) {
            return $q(function (resolve, reject) {
                const requiredName =
                    user.name ||
                    // if user was created from the Cognito console
                    // the name will be missing during FORCE_CHANGE_PASSWORD
                    // workflow. Use email as name for temp workaround
                    //
                    _.get(user, "_amplify.challengeParam.userAttributes.email");
                return Auth.completeNewPassword(user._amplify, newPassword, {
                    name: requiredName,
                }).then(resolve, reject);
            });
        }

        function goToState(stateName) {
            // unset cached user
            user = null;

            $state.go(
                stateName,
                {},
                {
                    location: "replace",
                }
            );
        }

        function goToLoginState(nextStateName, nextStateParams) {
            if ($state.is("account.login")) {
                return false;
            }

            // unset cached user
            user = null;

            $state.go(
                "account.login",
                {
                    name: nextStateName,
                    params: toJson(nextStateParams),
                },
                {
                    location: "replace",
                }
            );

            return true;
        }

        function requestResetPassword(username) {
            return $q(function (resolve, reject) {
                return Auth.forgotPassword(username).then(resolve, reject);
            });
        }

        function resetPassword(username, code, newPassword) {
            return $q(function (resolve, reject) {
                return Auth.forgotPasswordSubmit(
                    username,
                    code,
                    newPassword
                ).then(resolve, reject);
            });
        }

        function capitalize(str) {
            return str.charAt(0).toUpperCase() + str.slice(1);
        }

        function signUp(user) {
            return $q(function (resolve, reject) {
                if (!user) {
                    return reject(new Error("MissingRequiredParameter:: user"));
                }

                /*
                 * Lately we are having more and more users who are signing up
                 * without first name (or sometimes even without last name).
                 *   -> https://sablono.atlassian.net/browse/SAB-3062
                 *
                 * We don't know how this is possible because the UI form validation is
                 * not allowing that. But if it slips through, we need to make sure that the
                 * created user has valid first and last name.
                 *
                 * So this is kind of a hacky warkaround which is based on the assumption
                 * that the email address must be valid and given. We just take something
                 * meaningful from the email address and use it to create the missing name parts.
                 *
                 * This can be considered temporary and will be removed once the new login UI goes live
                 *   -> https://sablono.atlassian.net/browse/SAB-2501
                 */
                if (
                    !_.isString(user.firstName) ||
                    user.firstName.trim().length === 0
                ) {
                    // find first name in email
                    const name = user.email.split("@")[0];
                    user.firstName = capitalize(name.split(".")[0]);
                }

                if (
                    !_.isString(user.lastName) ||
                    user.lastName.trim().length === 0
                ) {
                    // find last name in email
                    const name = user.email.split("@")[0];
                    const names = name.split(".");
                    user.lastName = capitalize(names[names.length - 1]);
                }

                return Auth.signUp({
                    username: user.email,
                    password: user.password,
                    attributes: {
                        email: user.email,
                        name: user.firstName + " " + user.lastName,
                        "custom:company": user.company,
                        "custom:language_code": user.language,
                        "custom:signup_origin": user.signupOrigin,
                        "custom:job_title": user.otherJobTitle
                            ? user.otherJobTitle
                            : user.jobTitle,
                        "custom:company_type": user.companyType,
                        "custom:phone": user.phone,
                        "custom:marketing_consent_v2": user.marketingConsent,
                        "custom:hs_analytics_source": user.analyticsSourceData,
                        "custom:hs_what_tracking": user.whatTracking,
                        "custom:hs_why_tracking": user.whyTracking,
                    },
                }).then(resolve, (err) => {
                    // Error originating from our pre sign-up Lambda trigger
                    //
                    if (err.code === "UserLambdaValidationException") {
                        if (
                            err.message.includes(
                                "Signup with this email domain is not allowed"
                            )
                        ) {
                            reject(
                                new PresentableError(
                                    "BlockedEmailDomainException",
                                    "SIGNUP_ERROR_EMAIL_DOMAIN_BLOCKED_TITLE",
                                    "SIGNUP_ERROR_EMAIL_DOMAIN_BLOCKED_TEXT"
                                )
                            );
                        }
                    }
                    reject(err);
                });
            });
        }

        function confirmSignUp(username, code) {
            return $q(function (resolve, reject) {
                return Auth.confirmSignUp(username, code, {}).then(
                    resolve,
                    reject
                );
            });
        }

        /**
         * Get current authentiated user object. It will throw an error if there is no user logged in.
         * @returns {Promise<CognitoAuthenticatedUser>}
         */
        function currentAuthenticatedUser() {
            return $q(function (resolve, reject) {
                return Auth.currentAuthenticatedUser().then(
                    resolve,
                    (error) => {
                        const authError = new AuthenticationError(
                            "(Legacy) Failed to get currently authenticated user."
                        );
                        authError.cause = error;
                        return reject(authError);
                    }
                );
            });
        }

        function updateUserAttributes(user, attributes) {
            return $q(function (resolve, reject) {
                return Auth.updateUserAttributes(user, attributes).then(
                    resolve,
                    reject
                );
            });
        }

        function changePassword(user, oldPassword, newPassword) {
            return $q(function (resolve, reject) {
                return Auth.changePassword(user, oldPassword, newPassword).then(
                    resolve,
                    reject
                );
            });
        }

        function resendVerificationEmail(user) {
            return Auth.resendSignUp(user);
        }
    };
}
